import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Card, CardContent, Divider } from "@mui/material";

import MonitorHeartOutlinedIcon  from "@mui/icons-material/MonitorHeartOutlined";
import PaymentsOutlinedIcon      from "@mui/icons-material/PaymentsOutlined";
import SmsOutlinedIcon           from "@mui/icons-material/SmsOutlined";
import GroupsOutlinedIcon        from "@mui/icons-material/GroupsOutlined";
import AssessmentOutlinedIcon    from "@mui/icons-material/AssessmentOutlined";
import BugReportOutlinedIcon     from "@mui/icons-material/BugReportOutlined";
import TerminalIcon              from "@mui/icons-material/Terminal";
import ComputerOutlinedIcon      from "@mui/icons-material/ComputerOutlined";
import ReceiptLongOutlinedIcon   from "@mui/icons-material/ReceiptLongOutlined";
import CreditCardOutlinedIcon    from "@mui/icons-material/CreditCardOutlined";
import QuestionAnswerOutlinedIcon from "@mui/icons-material/QuestionAnswerOutlined";
import BarChartOutlinedIcon      from "@mui/icons-material/BarChartOutlined";
import LinkOutlinedIcon           from "@mui/icons-material/LinkOutlined";
import LocalShippingOutlinedIcon  from "@mui/icons-material/LocalShippingOutlined";
import CampaignOutlinedIcon       from "@mui/icons-material/CampaignOutlined";

import SetupTyroModal          from "@/components/modals/admin/SetupTyroModal";
import CreateXeroContactModal  from "@/components/modals/admin/CreateXeroContactModal";
import StripePortalLinkModal   from "@/components/modals/admin/StripePortalLinkModal";
import ChemistListModal        from "@/components/modals/admin/ChemistListModal";
import LogModal                from "@/components/modals/admin/LogModal";
import GlobalHeadersModal      from "@/components/modals/admin/GlobalHeadersModal";
import AdminDefaultTemplatesPage from "@/pages/admin/functions/AdminDefaultTemplatesPage";
import { PHARMACY_HALO_COLORS } from "@/config/colors";

const BLUE = PHARMACY_HALO_COLORS.BLUE;

// ─── Section definitions ───────────────────────────────────────────────────
const SECTIONS = [
  { id: "analytics",      label: "Analytics",       Icon: AssessmentOutlinedIcon,   color: "#15803d" },
  { id: "finance",        label: "Finance",         Icon: PaymentsOutlinedIcon,     color: "#d97706" },
  { id: "system",         label: "System",         Icon: MonitorHeartOutlinedIcon, color: "#475569" },
  { id: "people",         label: "People",          Icon: GroupsOutlinedIcon,       color: "#0891b2" },
];

// ─── Reusable action card ──────────────────────────────────────────────────
function ActionCard({ icon: Icon, title, description, color, onClick }) {
  return (
    <Card
      variant="outlined"
      component="button"
      onClick={onClick}
      sx={{
        textAlign: "left",
        cursor: "pointer",
        border: "1.5px solid #e2e8f0",
        borderRadius: 2,
        p: 0,
        background: "white",
        transition: "all 0.15s",
        width: "100%",
        "&:hover": {
          borderColor: color,
          boxShadow: `0 0 0 3px ${color}18`,
          transform: "translateY(-1px)",
        },
      }}
    >
      <CardContent sx={{ p: "16px !important" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 38, height: 38, borderRadius: "10px",
              bgcolor: `${color}15`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 21, color }} />
          </Box>
          <Typography fontWeight={700} sx={{ fontSize: "0.93rem", color: "#1e293b", lineHeight: 1.3 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.82rem", lineHeight: 1.5 }}>
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ─── Section wrapper ───────────────────────────────────────────────────────
function Section({ id, label, Icon, color, children, sectionRef }) {
  return (
    <section ref={sectionRef} id={id} style={{ scrollMarginTop: 24 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 34, height: 34, borderRadius: "9px",
            bgcolor: `${color}15`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 19, color }} />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: "#1e293b", fontSize: "1rem" }}>
          {label}
        </Typography>
      </Box>
      {children}
    </section>
  );
}

// ─── Main component ────────────────────────────────────────────────────────
const Admin = () => {
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState({
    setupTyro: false, xeroContact: false, errorLog: false, debugLog: false, chemistList: false, stripePortal: false, globalHeaders: false,
  });
  const toggle = (key, val) => setModalOpen((p) => ({ ...p, [key]: val }));

  // Refs for each section (for scroll + IntersectionObserver)
  const refs = {
    analytics:      useRef(null),
    finance:        useRef(null),
    system:         useRef(null),
    people:         useRef(null),
  };

  const [activeSection, setActiveSection] = useState("analytics");

  // IntersectionObserver — highlight the section that is most in view
  useEffect(() => {
    const observers = [];
    SECTIONS.forEach(({ id }) => {
      const el = refs[id].current;
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: "-30% 0px -60% 0px", threshold: 0 }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollTo = (id) => {
    refs[id].current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  return (
    <div className="flex h-full min-h-0 overflow-hidden bg-white">

      {/* ── Left sticky mini-nav ── */}
      <aside className="w-44 flex-shrink-0 border-r border-slate-100 overflow-y-auto py-5 px-3 hidden sm:flex flex-col gap-1">
        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest px-2 mb-2">
          Admin Tools
        </p>
        {SECTIONS.map(({ id, label, Icon, color }) => {
          const active = activeSection === id;
          return (
            <button
              key={id}
              onClick={() => scrollTo(id)}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left text-sm font-medium transition-all w-full ${
                active
                  ? "bg-slate-100 text-slate-900"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"
              }`}
            >
              <Icon
                sx={{
                  fontSize: 17,
                  color: active ? color : "#94a3b8",
                  flexShrink: 0,
                  transition: "color 0.15s",
                }}
              />
              <span>{label}</span>
              {active && (
                <span
                  className="ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: color }}
                />
              )}
            </button>
          );
        })}
      </aside>

      {/* ── Right scrollable content ── */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6 space-y-10">

          {/* ── Analytics ── */}
          <Section id="analytics" label="Analytics" Icon={AssessmentOutlinedIcon} color="#15803d" sectionRef={refs.analytics}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionCard
                icon={BarChartOutlinedIcon}
                title="Reports"
                description="SMS billing, terminal analytics, dispense system distribution and installation trends."
                color="#15803d"
                onClick={() => navigate("/admin-functions/reports")}
              />
              <ActionCard
                icon={QuestionAnswerOutlinedIcon}
                title="CherubAI Gaps"
                description="Review AI conversation gaps and unresolved patient queries."
                color="#0ea5e9"
                onClick={() => navigate("/admin-functions/cherub")}
              />
            </div>
          </Section>

          <Divider />

          {/* ── Finance ── */}
          <Section id="finance" label="Finance" Icon={PaymentsOutlinedIcon} color="#d97706" sectionRef={refs.finance}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionCard
                icon={ReceiptLongOutlinedIcon}
                title="Create Xero Contact"
                description="Create a Xero contact and update the invoice prefix for a pharmacy."
                color="#7c3aed"
                onClick={() => toggle("xeroContact", true)}
              />
              <ActionCard
                icon={CreditCardOutlinedIcon}
                title="Setup Tyro (HaloPay)"
                description="Configure Tyro merchant and API credentials for a pharmacy."
                color="#d97706"
                onClick={() => toggle("setupTyro", true)}
              />
              <ActionCard
                icon={LinkOutlinedIcon}
                title="Get Stripe chemist portal link"
                description="Generate a Stripe customer portal link for a pharmacy to manage their billing."
                color="#7c3aed"
                onClick={() => toggle("stripePortal", true)}
              />
            </div>
          </Section>

          <Divider />

          {/* ── System ── */}
          <Section id="system" label="System" Icon={MonitorHeartOutlinedIcon} color="#475569" sectionRef={refs.system}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionCard
                icon={BugReportOutlinedIcon}
                title="Error Log"
                description="Browse and search system error logs."
                color="#dc2626"
                onClick={() => toggle("errorLog", true)}
              />
              <ActionCard
                icon={TerminalIcon}
                title="Debug Log"
                description="Browse system debug and diagnostic output."
                color="#475569"
                onClick={() => toggle("debugLog", true)}
              />
              <ActionCard
                icon={MonitorHeartOutlinedIcon}
                title="Trial Health"
                description="Monitor active pharmacy trials and their health status."
                color="#0891b2"
                onClick={() => navigate("/admin-functions/trial-health")}
              />
              <ActionCard
                icon={ComputerOutlinedIcon}
                title="Inactive Terminals"
                description="View and manage terminals that have gone inactive."
                color="#64748b"
                onClick={() => navigate("/admin-functions/inactive-terminals")}
              />
              <ActionCard
                icon={CampaignOutlinedIcon}
                title="Global Headers"
                description="Update or clear the global header / warning banners shown inside Quda, Zephyr, DocLink (Medmate, HubMed, Hola) and MediDrop."
                color="#0e7490"
                onClick={() => toggle("globalHeaders", true)}
              />
            </div>
          </Section>

          <Divider />

          {/* ── People ── */}
          <Section id="people" label="People" Icon={GroupsOutlinedIcon} color="#0891b2" sectionRef={refs.people}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <ActionCard
                icon={GroupsOutlinedIcon}
                title="Group Managers"
                description="Create managers, configure role permissions, and link them to pharmacies."
                color={BLUE}
                onClick={() => navigate("/admin/group-managers")}
              />
              <ActionCard
                icon={LocalShippingOutlinedIcon}
                title="ChemLink Groups"
                description="Create and manage ChemLink groups. Assign chemists and configure delivery managers for multi-pharmacy routes."
                color="#e87c57"
                onClick={() => navigate("/admin-functions/chem-link")}
              />
            </div>
          </Section>

          {/* Bottom padding so last section scrolls fully into view */}
          <div className="h-24" />
        </div>
      </main>

      {/* ── Modals (unchanged) ── */}
      <SetupTyroModal        isOpen={modalOpen.setupTyro}   onClose={() => toggle("setupTyro", false)} />
      <CreateXeroContactModal isOpen={modalOpen.xeroContact} onClose={() => toggle("xeroContact", false)} />
      <ChemistListModal      isOpen={modalOpen.chemistList} onClose={() => toggle("chemistList", false)} />
      <StripePortalLinkModal isOpen={modalOpen.stripePortal} onClose={() => toggle("stripePortal", false)} />
      <LogModal isOpen={modalOpen.errorLog}  onClose={() => toggle("errorLog", false)}  logType="Error" />
      <LogModal isOpen={modalOpen.debugLog}  onClose={() => toggle("debugLog", false)}  logType="Debug" />
      <GlobalHeadersModal isOpen={modalOpen.globalHeaders} onClose={() => toggle("globalHeaders", false)} />
    </div>
  );
};

export default Admin;
